/**
 * Recovery for lazy route chunks that fail to load after a deploy.
 *
 * Every release gives Vite's code-split chunks new content hashes and removes
 * the previous build's files. A tab opened before the deploy still holds the old
 * HTML, so the next lazy route it imports requests a filename that no longer
 * exists — `Failed to fetch dynamically imported module`.
 *
 * Scope, precisely (the surrounding comments used to overstate this):
 *
 * - This only helps **lazily-imported route chunks** (`App.tsx`'s `React.lazy`
 *   calls). If the *entry* chunk itself 404s, this module never executes and
 *   only the `no-store`-ish header in `lib/requestGuards.mjs` can help.
 * - React's hydration-mismatch errors (#418/#423) are handled internally by
 *   React re-rendering on the client. They are not promise rejections and do not
 *   dispatch `vite:preloadError`, so nothing here intercepts them.
 * - In `pnpm dev` neither listener fires: Vite's preload helper is build-only,
 *   and a failed lazy import surfaces as a render throw instead.
 *
 * Reloading on that specific failure turns a dead page into a brief flicker. The
 * reload is bounded by both a cooldown *and* a total attempt cap, because the
 * failing import is usually a lazy route that only fires on navigation — a
 * time-only limit would let a permanently-missing chunk reload forever, and each
 * reload costs a full SSR render, not a cheap static request.
 */

/** sessionStorage key holding the JSON-encoded {@link ReloadMarker}. */
export const RELOAD_MARKER_KEY = 'slc:chunk-reload';

/**
 * Minimum gap between two auto-reloads — guards against an immediate re-failure
 * looping the tab.
 */
export const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Hard cap on auto-reloads within one {@link ATTEMPT_RESET_MS} window. Reached
 * only when reloading genuinely doesn't help (a chunk no pod will ever serve),
 * at which point looping is strictly worse than surfacing the failure.
 */
export const MAX_RELOAD_ATTEMPTS = 2;

/**
 * Idle period after which the attempt counter resets, so a later unrelated
 * deploy in a long-lived tab still gets a fresh budget.
 */
export const ATTEMPT_RESET_MS = 10 * 60 * 1000;

/**
 * Substrings browsers use for a failed dynamic `import()`. They differ per
 * engine, so match on all of them rather than one canonical message.
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
];

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

type ReloadMarker = { at: number; count: number };

export type RecoveryDeps = {
  /** `null` when storage is unavailable — see {@link readSessionStorage}. */
  storage: StorageLike | null;
  reload: () => void;
  now: () => number;
  isOnline: () => boolean;
};

/**
 * Reads a message from whatever a rejection carries.
 *
 * Deliberately structural rather than `instanceof Error`: that check is
 * realm-local, so an `import()` rejecting inside an iframe — or a library that
 * rejects with a plain `{ message }` object — would otherwise be missed.
 */
const toMessage = (reason: unknown): string => {
  if (typeof reason === 'string') return reason;
  if (typeof reason === 'object' && reason !== null) {
    const { message } = reason as { message?: unknown };
    if (typeof message === 'string') return message;
  }
  return '';
};

/** True when `reason` is a chunk-load failure rather than an app-level error. */
export const isChunkLoadError = (reason: unknown): boolean => {
  const message = toMessage(reason).toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

/**
 * Returns `window.sessionStorage`, or `null` when it is unreachable.
 *
 * The *property access itself* throws — not just `getItem`/`setItem` — when
 * storage is denied (Chrome with third-party cookies blocked raises
 * `SecurityError`, as does a sandboxed iframe without `allow-same-origin`).
 * Since this runs from module scope in `entry-client.tsx` before `hydrateRoot`,
 * an uncaught throw here would abort the entry module and blank the page
 * permanently — the exact failure this module exists to prevent.
 */
export const readSessionStorage = (): StorageLike | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * Parses the stored marker, treating anything untrustworthy as "never
 * reloaded".
 *
 * `Number(...) || 0` is not enough: `Infinity` is truthy and survives it, and
 * `now - Infinity` is `-Infinity`, which is below any cooldown — that would
 * disable recovery for the rest of the session. A timestamp in the future does
 * the same and is reachable without tampering, via an NTP correction or a manual
 * clock change, so it is clamped rather than trusted.
 */
const readMarker = (storage: StorageLike, now: number): ReloadMarker => {
  const empty = { at: 0, count: 0 };

  const raw = storage.getItem(RELOAD_MARKER_KEY);
  if (!raw) return empty;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return empty;
  }

  if (typeof parsed !== 'object' || parsed === null) return empty;

  const { at, count } = parsed as { at?: unknown; count?: unknown };
  const safeAt = typeof at === 'number' && Number.isFinite(at) && at <= now ? at : 0;
  const safeCount = typeof count === 'number' && Number.isFinite(count) && count > 0 ? count : 0;

  // An idle gap (or a clamped/bogus timestamp) restores the full budget.
  if (safeAt === 0 || now - safeAt > ATTEMPT_RESET_MS) return empty;

  return { at: safeAt, count: safeCount };
};

/**
 * Reloads once for a chunk-load failure, honouring the cooldown and attempt cap.
 *
 * Returns whether a reload was triggered so callers can tell a recovery from a
 * deliberate no-op (and suppress Vite's rethrow only when the page is genuinely
 * about to navigate away).
 */
export const recoverFromChunkLoadFailure = ({ storage, reload, now, isOnline }: RecoveryDeps): boolean => {
  // Offline, a reload lands the user on the browser's error page and destroys
  // in-memory state. `vite:preloadError` fires for any preload failure, so this
  // is a routine case, not an edge case.
  if (!isOnline() || storage === null) return false;

  const currentTime = now();

  let marker: ReloadMarker;
  try {
    marker = readMarker(storage, currentTime);
  } catch {
    return false;
  }

  if (marker.count >= MAX_RELOAD_ATTEMPTS) return false;
  if (marker.at !== 0 && currentTime - marker.at < RELOAD_COOLDOWN_MS) return false;

  const next: ReloadMarker = { at: currentTime, count: marker.count + 1 };
  try {
    storage.setItem(RELOAD_MARKER_KEY, JSON.stringify(next));
  } catch {
    // Without a durable marker there is no rate limit, and an unbounded reload
    // loop is worse than the blank page.
    return false;
  }

  reload();
  return true;
};

/** Builds the production dependency set, tolerating unavailable storage. */
const defaultDeps = (): RecoveryDeps => ({
  storage: readSessionStorage(),
  reload: () => window.location.reload(),
  now: () => Date.now(),
  isOnline: () => navigator.onLine !== false,
});

/**
 * Subscribes to the two routes a chunk failure can surface through, and returns
 * a disposer so callers (and tests) can unsubscribe.
 *
 * In practice `vite:preloadError` is the one that fires: `React.lazy` attaches
 * its own handlers to the `import()` promise, so a failed route chunk is an
 * already-handled rejection and never reaches `unhandledrejection`. That
 * listener still covers the bare `import()` calls in `entry-client.tsx` and any
 * future un-awaited dynamic import.
 */
export const registerChunkReloadRecovery = (deps: RecoveryDeps = defaultDeps()): (() => void) => {
  const onPreloadError = (event: Event) => {
    // Only suppress Vite's rethrow when we are actually navigating away;
    // otherwise let it propagate so the failure stays visible.
    if (recoverFromChunkLoadFailure(deps)) event.preventDefault();
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) recoverFromChunkLoadFailure(deps);
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
};
